'use strict';
import defaults from 'lodash/defaults';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import has from 'lodash/has';
import {getUserId} from '../../auth/reducers/state';
import {getId} from '../domain/ReduxUtils';


const angular = require('angular');
const {copy} = angular;

export class RestUtils {
  static $inject = [
    'FirebaseConnectionService',
    'fetchUtils',
    'FirebaseQueueService',
    '$ngRedux',
    'ROOT_API_COLLECTION',
    'ROOT_API_SPEC',
  ];

  constructor(
    FirebaseConnectionService,
    fetchUtils,
    FirebaseQueueService,
    $ngRedux,
    ROOT_API_COLLECTION,
    ROOT_API_SPEC,
  ) {
    this.FirebaseConnectionService = FirebaseConnectionService;
    this.fetchUtils = fetchUtils;
    this.FirebaseQueueService = FirebaseQueueService;
    this.$ngRedux = $ngRedux;
    this.ROOT_API_COLLECTION = ROOT_API_COLLECTION;
    this.ROOT_API_SPEC = ROOT_API_SPEC;
  }

  /**
   * @typedef {Object} RestUtilsCreateOptions
   * @property {RestUtils~getCreateBody} getBody
   * @property {RestUtils~afterSuccess} [afterSuccess]
   * @property {Boolean} [canUseFallback=true]
   */

  /**
   * @callback RestUtils~getCreateBody
   * @param {Object} instance
   * @param {Object} options
   * @param {$ngRedux} $ngRedux
   * @return {Object}
   */

  /**
   * afterSuccess may not modify the instance returned. This interceptor is intended
   * for temporary inter-op between the legacy system and redux. Typical usage will
   * be to insert the instance into the corresponding redux list state.
   *
   * @callback RestUtils~afterSuccess
   * @param {Object} instance
   */

  /**
   * @callback RestUtils~requestCreate
   * @param {Object} instance
   * @param {Object} options
   * @return {Promise}
   */

  /**
   * @param domainClass
   * @param {RestUtilsCreateOptions} o
   * @return {RestUtils~requestCreate}
   */
  create(domainClass, o) {
    /** @type {RestUtilsCreateOptions} options **/
    const options = {};
    defaults(options, o, {
      canUseFallback: true,
    });

    // Validate options
    if (!isObject(domainClass) || !isString(domainClass.COLLECTION))
      throw new Error('restUtils.create missing required domainClass');
    if (!isFunction(options.getBody))
      throw new Error('restUtils.create missing required getBody');
    if (has(options, 'afterSuccess') && !isFunction(options.afterSuccess))
      throw new Error('restUtils.create expects a function for afterSuccess');

    const fetchUtils = this.fetchUtils;
    // needed for fallback only
    const FirebaseConnectionService = this.FirebaseConnectionService;
    const FirebaseQueueService = this.FirebaseQueueService;
    const $ngRedux = this.$ngRedux;
    const ROOT_API_COLLECTION = this.ROOT_API_COLLECTION;
    const ROOT_API_SPEC = this.ROOT_API_SPEC;

    return function (instance, data) {
      const bodyData = options.getBody(instance, data, $ngRedux);
      const request = {
        method: 'POST',
        mode: 'cors',
        cache: 'default',
        contentType: 'json',
        responseType: 'json',
        body: bodyData,
      };

      const url = `${fetchUtils.API_URL}/api/${domainClass.COLLECTION}`;
      let fetchPromise = fetchUtils.requestWithAuth(url, request);
      if (options.canUseFallback === true)
        fetchPromise = fetchPromise.catch(firebaseFallback);
      /* Temporary fallback */
      const makeRequest = fetchPromise
        .then(response => {
          if (response.status === 200) {
            return domainClass.$get(response.id);
          } else if (response.statusCode === 200) {
            return domainClass.$get(response[domainClass.fallbackIdProperty]);
          }
        });

      if (isFunction(options.afterSuccess)) {
        return makeRequest.then(afterSuccess);
      } else {
        return makeRequest;
      }

      function firebaseFallback(error) {
        if (!isString(domainClass.createFallbackMethod))
          return Promise.reject(error);

        if (!FirebaseConnectionService.online)
          return Promise.reject({code: 'default.error.offline.message'});
        const fallbackRequest = JSON.parse(bodyData);
        fallbackRequest.method = domainClass.createFallbackMethod;
        if (domainClass.COLLECTION.indexOf('requirements') > -1)
          fallbackRequest.requirementType = 'file';
        return FirebaseQueueService
          .addTask(fallbackRequest, getUserId($ngRedux.getState()), ROOT_API_COLLECTION, ROOT_API_SPEC)
          .then(fetchUtils.logFallbackUsage({url}));
      }

      function afterSuccess(newInstance) {
        options.afterSuccess(newInstance);
        return newInstance;
      }
    };
  }

  /**
   * @param domainClass
   * @param {RestUtilsCreateOptions} options
   * @return {RestUtils~requestCreate}
   */
  createProto(domainClass, options) {
    const createStatic = this.create(domainClass, options);

    return function (options) {
      // not the same this
      return createStatic(this, options);
    };
  }

  createPut(domainClass, getBody) {
    const fetchUtils = this.fetchUtils;
    const $ngRedux = this.$ngRedux;

    return function (instance, options) {
      const id = options.id;
      const bodyData = getBody(instance, options, $ngRedux);
      const request = {
        method: 'PUT',
        mode: 'cors',
        cache: 'default',
        contentType: 'json',
        responseType: 'json',
        body: bodyData,
      };

      const url = `${fetchUtils.API_URL}/api/${domainClass.COLLECTION}/${id}`;
      return fetchUtils.requestWithAuth(url, request)
        .then(response => {
          if (response.status === 200 || response.statusCode === 200)
            return domainClass.$get(id);
        });
    };
  }

  createPutProto(domainClass, getBody) {
    const createPutStatic = this.createPut(domainClass, getBody);

    return function (options) {
      // not the same this
      return createPutStatic(this, options);
    };
  }

  /**
   * @typedef {Object} RestUtilsUpdateOptions
   * @property {RestUtils~afterSuccess} [afterSuccess]
   * @property {Boolean} [canUseFallback=true]
   */

  /**
   * @callback RestUtils~requestUpdate
   * @param {Object} instance
   * @return {Promise}
   */

  /**
   * Static update method that does not interact with the Angular Domain library
   * @param {String} collection
   * @param {RestUtilsUpdateOptions} [options={}]
   * @return {RestUtils~requestUpdate}
   */
  updateStatic(collection, options = {}) {

    // Validate options
    if (!isString(collection))
      throw new Error('restUtils.update missing required collection');
    if (has(options, 'afterSuccess') && !isFunction(options.afterSuccess))
      throw new Error('restUtils.update expects a function for afterSuccess');

    const fetchUtils = this.fetchUtils;

    return function update(id, updateData) {
      const request = {
        method: 'PATCH',
        mode: 'cors',
        cache: 'default',
        contentType: 'json',
        responseType: 'json',
        body: updateData,
      };
      const url = `${fetchUtils.API_URL}/api/${collection}/${id}`;
      const fetchPromise = fetchUtils.requestWithAuth(url, request);

      if (isFunction(options.afterSuccess)) {
        return fetchPromise.then(afterSuccess);
      } else {
        return fetchPromise;
      }

      function afterSuccess(newInstance) {
        options.afterSuccess(newInstance);
        return newInstance;
      }
    };
  }

  /**
   * @param domainClass
   * @param {RestUtilsUpdateOptions} [o={}]
   * @return {RestUtils~requestUpdate}
   */
  update(domainClass, o = {}) {
    /** @type {RestUtilsUpdateOptions} options **/
    const options = {};
    defaults(options, o, {
      canUseFallback: true,
    });

    // Validate options
    if (!isObject(domainClass) || !isString(domainClass.COLLECTION))
      throw new Error('restUtils.update missing required domainClass');
    if (has(options, 'afterSuccess') && !isFunction(options.afterSuccess))
      throw new Error('restUtils.update expects a function for afterSuccess');

    const fetchUtils = this.fetchUtils;
    // needed for fallback only
    const FirebaseConnectionService = this.FirebaseConnectionService;
    const FirebaseQueueService = this.FirebaseQueueService;
    const $ngRedux = this.$ngRedux;
    const ROOT_API_COLLECTION = this.ROOT_API_COLLECTION;
    const ROOT_API_SPEC = this.ROOT_API_SPEC;

    return function update(instance) {
      if (!instance.$isDirty()) return Promise.resolve(null);
      if (!instance.$validate()) return Promise.reject(instance.$errors);

      const updateData = instance.$serialize();
      const request = {
        method: 'PATCH',
        mode: 'cors',
        cache: 'default',
        contentType: 'json',
        responseType: 'json',
        body: updateData,
      };

      const id = getId(instance);
      const url = `${fetchUtils.API_URL}/api/${domainClass.COLLECTION}/${id}`;
      let fetchPromise = fetchUtils.requestWithAuth(url, request);
      if (options.canUseFallback === true)
        fetchPromise = fetchPromise.catch(firebaseFallback);
      const makeRequest = fetchPromise
        .then((response => {
          if (isFunction(instance.$revert))
            instance.$revert();
          return response;
        }));

      if (isFunction(options.afterSuccess)) {
        return makeRequest.then(afterSuccess);
      } else {
        return makeRequest;
      }

      function firebaseFallback(error) {
        if (!isString(domainClass.updateFallbackMethod))
          return Promise.reject(error);

        if (!FirebaseConnectionService.online)
          return Promise.reject({code: 'default.error.offline.message'});
        const fallbackRequest = copy(updateData);
        fallbackRequest.method = domainClass.updateFallbackMethod;
        fallbackRequest.id = id;
        if (domainClass.COLLECTION.indexOf('requirements') > -1)
          fallbackRequest.requirementType = 'file';
        return FirebaseQueueService
          .addTask(fallbackRequest, getUserId($ngRedux.getState()), ROOT_API_COLLECTION, ROOT_API_SPEC)
          .then(fetchUtils.logFallbackUsage({url}));
      }

      function afterSuccess(newInstance) {
        options.afterSuccess(newInstance);
        return newInstance;
      }
    };
  }

  /**
   * @param domainClass
   * @param {RestUtilsUpdateOptions} [options={}]
   * @return {RestUtils~requestUpdate}
   */
  updateProto(domainClass, options = {}) {
    const updateStatic = this.update(domainClass, options);

    return function () {
      // not the same this
      return updateStatic(this);
    };
  }

  /**
   * @typedef {Object} RestUtilsUpdateStatusOptions
   * @property {RestUtils~afterSuccess} [afterSuccess]
   * @property {Boolean} [canUseFallback=true]
   */

  /**
   * @callback RestUtils~requestStatusUpdate
   * @param {Object} instance
   * @param {String} status
   * @param {?String} comment
   * @return {Promise}
   */

  /**
   * @param domainClass
   * @param {RestUtilsUpdateStatusOptions} [o={}]
   * @return {RestUtils~requestStatusUpdate}
   */
  updateStatus(domainClass, o = {}) {
    /** @type {RestUtilsUpdateStatusOptions} options **/
    const options = {};
    defaults(options, o, {
      canUseFallback: true,
    });

    // Validate options
    if (!isObject(domainClass) || !isString(domainClass.COLLECTION))
      throw new Error('restUtils.updateStatus missing required domainClass');
    if (has(options, 'afterSuccess') && !isFunction(options.afterSuccess))
      throw new Error('restUtils.updateStatus expects a function for afterSuccess');

    const fetchUtils = this.fetchUtils;
    // needed for fallback only
    const FirebaseConnectionService = this.FirebaseConnectionService;
    const FirebaseQueueService = this.FirebaseQueueService;
    const $ngRedux = this.$ngRedux;
    const ROOT_API_COLLECTION = this.ROOT_API_COLLECTION;
    const ROOT_API_SPEC = this.ROOT_API_SPEC;

    return function update(instance, status, comment) {
      const request = {
        method: 'PATCH',
        mode: 'cors',
        cache: 'default',
        contentType: 'json',
        responseType: 'json',
        body: isString(comment) ? {status, comment} : {status},
      };

      const id = getId(instance);
      const url = `${fetchUtils.API_URL}/api/${domainClass.COLLECTION}/status/${id}`;
      let fetchPromise = fetchUtils.requestWithAuth(url, request);
      if (options.canUseFallback === true)
        fetchPromise = fetchPromise.catch(firebaseFallback);
      const makeRequest = fetchPromise
        .then((response => {
          if (isFunction(instance.$revert))
            instance.$revert();
          return response;
        }));

      if (isFunction(options.afterSuccess)) {
        return makeRequest.then(afterSuccess);
      } else {
        return makeRequest;
      }

      function firebaseFallback() {
        if (!FirebaseConnectionService.online)
          return Promise.reject({code: 'default.error.offline.message'});
        const fallbackRequest = isString(comment) ? {status, comment} : {status};
        fallbackRequest.method = domainClass.updateStatusFallbackMethod;
        fallbackRequest[domainClass.fallbackIdProperty] = id;
        return FirebaseQueueService
          .addTask(fallbackRequest, getUserId($ngRedux.getState()), ROOT_API_COLLECTION, ROOT_API_SPEC)
          .then(fetchUtils.logFallbackUsage({url}));
      }

      function afterSuccess(newInstance) {
        options.afterSuccess(newInstance);
        return newInstance;
      }
    };
  }

  /**
   * @param domainClass
   * @param {RestUtilsUpdateStatusOptions} [options={}]
   * @return {RestUtils~requestStatusUpdate}
   */
  updateStatusProto(domainClass, options = {}) {
    const updateStatusStatic = this.updateStatus(domainClass, options);

    return (status, options) => updateStatusStatic(this, status, options);
  }

  deleteProto(domainClass) {

    // Validate options
    if (!isObject(domainClass) || !isString(domainClass.COLLECTION))
      throw new Error('restUtils.deleteProto missing required domainClass');

    const fetchUtils = this.fetchUtils;

    return function update() {
      const request = {
        method: 'DELETE',
        mode: 'cors',
        cache: 'default',
      };

      // not the same this
      const url = `${fetchUtils.API_URL}/api/${domainClass.COLLECTION}/${getId(this)}`;
      return fetchUtils.requestWithAuth(url, request)
        .catch(() => this.$delete().then(fetchUtils.logFallbackUsage({url})))/* Temporary fallback */;
    };
  }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/fetch-utils/RestUtils.js